import React, { useCallback, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  Box,
  Select,
  Grid,
  FormControl,
  IconButton,
  Tooltip,
  HStack,
  Text,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import { Header, ColumnInfo, ModelInfo, ObservationColumnProto } from "build-ts/metadata";
import { AggregationType, ColumnType, columnTypeToJSON, DataType, dataTypeToJSON } from "build-ts/enums";
import DataModelMenuComponent from "./DataModelMenuComponent";
import CreateKGColumnComponent from "../mkg/CreateKGColumnComponent";
import EditMetricDefinitionContainer from "../../../containers/configuration/EditMetricDefinitionContainer";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";

interface AddDescriptionProps {
  loading: boolean;
  modelInfo?: ModelInfo | undefined;
  addedColumns?: ColumnInfo[] | undefined;
  removedColumns?: ColumnInfo[] | undefined;
  header: Header;
  columns: ColumnInfo[];
  kgColumns: Header[];
  dependentKgColumns: Map<string, Header[]>;
  dependentMetrics: Map<string, Header[]>;
  handleHeaderChange: (property: string, value: string) => void;
  handleColumnChange: (index: number, property: "data_type" | "column_type" | "default_aggregation", value: string) => void;
  handleKGColumnChange: (
    index: number,
    kgColumnId: string,
    kgColumnHeader: Header | null
  ) => void;
  handleSubmit: () => void;
  updateFlag: boolean;
  buttonLoader: boolean;
  refreshKGColumns: () => Promise<void>;
  initializeColumns?: () => Promise<void>;
  initializeMatchingColumns?: () => Promise<void>;
  // Queues a full metric request for creation after model save (used when updateFlag=false)
  handleQueueMetricRequest: (request: CreateOrUpdateMetrics_Request) => void;
  handleMetricChange: (index: number, metricHeader: Header | null) => void;
  selectedMetrics: { [columnIndex: number]: Header };
  metricRequestList: CreateOrUpdateMetrics_Request[];
  onBack: () => void;
}

const AddDescription: React.FC<AddDescriptionProps> = ({
  loading,
  modelInfo,
  addedColumns,
  removedColumns,
  header,
  columns,
  kgColumns,
  dependentKgColumns,
  dependentMetrics,
  handleHeaderChange,
  handleColumnChange,
  handleKGColumnChange,
  handleSubmit,
  updateFlag,
  buttonLoader,
  refreshKGColumns,
  initializeColumns,
  initializeMatchingColumns,
  handleQueueMetricRequest,
  handleMetricChange,
  selectedMetrics,
  metricRequestList,
  onBack
}) => {
  const navigate = useNavigate();

  // --- KG Column modal state ---
  const { isOpen: isKGModalOpen, onOpen: onKGModalOpen, onClose: onKGModalClose } = useDisclosure();
  const [kgModalColumnIndex, setKgModalColumnIndex] = useState<number | null>(null);
  const [kgModalColumnType, setKgModalColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  const [kgModalDefaultName, setKgModalDefaultName] = useState<string>("");

  const handleOpenCreateKGColumnModal = (columnType: ColumnType, columnIndex: number, defaultName: string) => {
    setKgModalColumnIndex(columnIndex);
    setKgModalColumnType(columnType);
    setKgModalDefaultName(defaultName);
    onKGModalOpen();
  };

  const handleKGColumnCreated = (createdHeader: Header) => {
    if (kgModalColumnIndex !== null) {
      handleKGColumnChange(kgModalColumnIndex, createdHeader.id!, createdHeader);
    }
    onKGModalClose();
  };

  // --- Metric modal state ---
  const { isOpen: isMetricModalOpen, onOpen: onMetricModalOpen, onClose: onMetricModalClose } = useDisclosure();
  const [metricModalColumnIndex, setMetricModalColumnIndex] = useState<number | null>(null);
  const [metricModalLogicalColumn, setMetricModalLogicalColumn] = useState<ColumnInfo | null>(null);

  const handleOpenCreateMetricModal = (columnIndex: number, logicalColumn: ColumnInfo) => {
    setMetricModalColumnIndex(columnIndex);
    setMetricModalLogicalColumn(logicalColumn);
    onMetricModalOpen();
  };

  // Case 1 (model exists, updateFlag=true): metric was created via API — auto-map it
  const handleMetricCreated = (createdHeader: Header) => {
    if (metricModalColumnIndex !== null) {
      handleMetricChange(metricModalColumnIndex, createdHeader);
    }
    onMetricModalClose();
  };

  // Case 2 (model not yet saved, updateFlag=false): queue the full request; show it optimistically
  const handleMetricDeferred = (formData: CreateOrUpdateMetrics_Request) => {
    handleQueueMetricRequest(formData);
    // Optimistically mark the metric as selected so the UI reflects the pending creation
    if (metricModalColumnIndex !== null && formData.metrics[0]?.header) {
      handleMetricChange(metricModalColumnIndex, formData.metrics[0].header);
    }
    onMetricModalClose();
  };

  // IDs of added columns for fast lookup — used to split columns into added vs regular
  const addedColumnIds = new Set((addedColumns ?? []).map(c => c.header?.id).filter(Boolean));

  // Added columns that are already in the columns array (shown at top, green)
  const addedInColumns = columns.filter(c => addedColumnIds.has(c.header?.id));
  // Regular columns — everything else
  const regularColumns = columns.filter(c => !addedColumnIds.has(c.header?.id));

  // Helper function to get combined metrics (existing + newly created)
  const getCombinedMetrics = useCallback((columnId: string): Header[] => {
    // Get existing metrics from dependencies
    const existingMetrics = dependentMetrics.get(columnId) || [];

    // Get newly created metrics from metricRequestList for this column
    const newMetrics: Header[] = [];
    metricRequestList.forEach(request => {
      request.metrics?.forEach((metric) => {
        // Check if this metric is for the current column
        const observationColumns = metric.definition?.observation_columns || [];
        const isForThisColumn = observationColumns.some((obsCol) =>
          obsCol.logical_column_header?.id === columnId
        );

        if (isForThisColumn && metric.header) {
          newMetrics.push(metric.header);
        }
      });
    });

    return [...existingMetrics, ...newMetrics];
  }, [dependentMetrics, metricRequestList]);

  const handleColumnTypeChange = async (index: number, property: "data_type" | "column_type" | "default_aggregation", value: string) => {
    // Handle the column change first
    handleColumnChange(index, property, value);

    // If column type is changed, re-initialize columns and matching columns
    if (property === "column_type") {
      if (initializeColumns) {
        await initializeColumns();
      }
      if (initializeMatchingColumns) {
        await initializeMatchingColumns();
      }
    }
  };

  return (
    <Box mt={4} ml={2}>
      <Box>
        <HStack spacing={3} justify="flex-end">
          <Button
            variant="outline"
            colorScheme="purple"
            onClick={onBack}
            leftIcon={<Box as="span">←</Box>}
          >
            Back
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={buttonLoader}
            width="120px"
            mr={2}
          >
            {updateFlag ? "Update" : "Create"}
          </Button>
        </HStack>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Spinner size="md" color="purple.500" thickness="2px" mr={2} />
          <Text color="gray.600" fontSize="md" m={0}>Loading model information...</Text>
        </Box>
      ) : (
        <Box>
          <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={3}
            mb={0}
            mt={2}
          >
            <FormControl id="header-name" isRequired>
              <Input
                placeholder="Enter header name"
                value={header?.name}
                onChange={(e) => handleHeaderChange("name", e.target.value)}
              />
            </FormControl>

            <FormControl id="header-description">
              <Input
                placeholder="Enter header description"
                value={header?.description || ""}
                onChange={(e) => handleHeaderChange("description", e.target.value)}
              />
            </FormControl>
          </Grid>
          <HStack spacing={2} justify="flex-end">
            <Tooltip label="Add Formula Column" placement="top" hasArrow bg="gray.700" color="white" fontSize="sm" p={2} borderRadius="md">
              <IconButton
                aria-label="Add Formula Column"
                icon={<FaPlus size={15} />}
                variant="outline"
                size="sm"
                colorScheme="purple"
                onClick={() => navigate(`/mkg/thoughtspot/${modelInfo?.data_source?.ts_worksheet_id}`)}
                mr={4}
                mt={2}
                mb={2}
              />
            </Tooltip>
          </HStack>

          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Column Name</Th>
                <Th>Data Type</Th>
                <Th>Column Type</Th>
                <Th>KG Column</Th>
                <Th>Metric</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Removed columns — red, read-only, shown at top */}
              {(removedColumns ?? []).map((col, i) => (
                <Tr key={`removed-${col.header?.id ?? i}`} bg="red.50">
                  <Td>
                    <HStack spacing={2}>
                      <Text m={0} color="red.700" fontWeight="500">
                        {col.header?.name || ""}
                      </Text>
                      <Badge colorScheme="red" fontSize="9px" variant="subtle">
                        removed
                      </Badge>
                    </HStack>
                  </Td>
                  <Td>
                    <Text m={0} color="red.400" fontSize="sm">
                      {(col.data_type!)}
                    </Text>
                  </Td>
                  <Td>
                    <Text m={0} color="red.400" fontSize="sm">
                      {(col.column_type!)}
                    </Text>
                  </Td>
                  <Td colSpan={2} />
                </Tr>
              ))}

              {/* Added columns — green, editable, shown after removed */}
              {addedInColumns.map((column) => {
                const index = columns.findIndex(c => c.header?.id === column.header?.id);
                return (
                  <Tr key={`added-${column.header?.id ?? index}`} bg="green.50">
                    <Td>
                      <HStack spacing={2}>
                        <Text m={0} color="green.700" fontWeight="500">
                          {column.header?.name || ""}
                        </Text>
                        <Badge colorScheme="green" fontSize="9px" variant="subtle">
                          new
                        </Badge>
                      </HStack>
                    </Td>
                    <Td>
                      <Select
                        value={dataTypeToJSON(column.data_type!) || ""}
                        onChange={(e) => handleColumnChange(index, "data_type", e.target.value)}
                        size="sm"
                        bg="white"
                      >
                        <option value={dataTypeToJSON(DataType.VARCHAR)}>VARCHAR</option>
                        <option value={dataTypeToJSON(DataType.BOOLEAN)}>BOOLEAN</option>
                        <option value={dataTypeToJSON(DataType.INT32)}>INT32</option>
                        <option value={dataTypeToJSON(DataType.INT64)}>INT64</option>
                        <option value={dataTypeToJSON(DataType.DOUBLE)}>DOUBLE</option>
                        <option value={dataTypeToJSON(DataType.DATE)}>DATE</option>
                        <option value={dataTypeToJSON(DataType.DATETIME)}>DATETIME</option>
                      </Select>
                    </Td>
                    <Td>
                      <Select
                        value={columnTypeToJSON(column.column_type!) || ""}
                        onChange={(e) => handleColumnTypeChange(index, "column_type", e.target.value)}
                        size="sm"
                        bg="white"
                      >
                        <option value={columnTypeToJSON(ColumnType.ATTRIBUTE)}>ATTRIBUTE</option>
                        {(column.data_type === DataType.INT32 || column.data_type === DataType.INT64 || column.data_type === DataType.DOUBLE) && (
                          <option value={columnTypeToJSON(ColumnType.MEASURE)}>MEASURE</option>
                        )}
                        {(column.data_type === DataType.DATE || column.data_type === DataType.DATETIME) && (
                          <option value={columnTypeToJSON(ColumnType.TEMPORAL)}>TEMPORAL</option>
                        )}
                      </Select>
                    </Td>
                    <Td>
                      <DataModelMenuComponent
                        type="KG_COLUMN"
                        logicalColumnList={columns}
                        kgColumnList={kgColumns}
                        selectedItem={dependentKgColumns.get(column.header?.id!)?.[0] || null}
                        columnType={column.column_type}
                        columnIndex={index}
                        logicalColumn={column}
                        onItemSelect={(selectedKgColumn) => {
                          if (selectedKgColumn) {
                            handleKGColumnChange(index, selectedKgColumn.id!, selectedKgColumn);
                          } else {
                            handleKGColumnChange(index, "", null);
                          }
                        }}
                        onOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
                        onOpenCreateMetricModal={handleOpenCreateMetricModal}
                        dependentKgColumns={dependentKgColumns}
                        placeholder="None"
                      />
                    </Td>
                    <Td>
                      {column.column_type === ColumnType.MEASURE && (
                        <DataModelMenuComponent
                          type="METRIC"
                          logicalColumnList={columns}
                          metricList={getCombinedMetrics(column.header?.id!)}
                          selectedItem={selectedMetrics[index] || null}
                          columnIndex={index}
                          logicalColumn={column}
                          onItemSelect={(selectedMetric) => handleMetricChange(index, selectedMetric)}
                          onOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
                          onOpenCreateMetricModal={handleOpenCreateMetricModal}
                          placeholder="None"
                        />
                      )}
                    </Td>
                  </Tr>
                );
              })}

              {/* Regular columns */}
              {regularColumns.map((column) => {
                const index = columns.findIndex(c => c.header?.id === column.header?.id);
                return (
                  <Tr key={index}>
                    <Td>
                      <Text m={0}>{column.header?.name || ""}</Text>
                    </Td>
                    <Td>
                      <Select
                        value={dataTypeToJSON(column.data_type!) || ""}
                        onChange={(e) => handleColumnChange(index, "data_type", e.target.value)}
                      >
                        <option value={dataTypeToJSON(DataType.VARCHAR)}>VARCHAR</option>
                        <option value={dataTypeToJSON(DataType.BOOLEAN)}>BOOLEAN</option>
                        <option value={dataTypeToJSON(DataType.INT32)}>INT32</option>
                        <option value={dataTypeToJSON(DataType.INT64)}>INT64</option>
                        <option value={dataTypeToJSON(DataType.DOUBLE)}>DOUBLE</option>
                        <option value={dataTypeToJSON(DataType.DATE)}>DATE</option>
                        <option value={dataTypeToJSON(DataType.DATETIME)}>DATETIME</option>
                      </Select>
                    </Td>
                    <Td>
                      <Select
                        value={columnTypeToJSON(column.column_type!) || ""}
                        onChange={(e) => handleColumnTypeChange(index, "column_type", e.target.value)}
                      >
                        <option value={columnTypeToJSON(ColumnType.ATTRIBUTE)}>ATTRIBUTE</option>
                        {(column.data_type === DataType.INT32 || column.data_type === DataType.INT64 || column.data_type === DataType.DOUBLE) && (
                          <option value={columnTypeToJSON(ColumnType.MEASURE)}>MEASURE</option>
                        )}
                        {(column.data_type === DataType.DATE || column.data_type === DataType.DATETIME) && (
                          <option value={columnTypeToJSON(ColumnType.TEMPORAL)}>TEMPORAL</option>
                        )}
                      </Select>
                    </Td>
                    <Td>
                      <DataModelMenuComponent
                        type="KG_COLUMN"
                        logicalColumnList={columns}
                        kgColumnList={kgColumns}
                        selectedItem={dependentKgColumns.get(column.header?.id!)?.[0] || null}
                        columnType={column.column_type}
                        columnIndex={index}
                        logicalColumn={column}
                        onItemSelect={(selectedKgColumn) => {
                          if (selectedKgColumn) {
                            handleKGColumnChange(index, selectedKgColumn.id!, selectedKgColumn);
                          } else {
                            handleKGColumnChange(index, "", null);
                          }
                        }}
                        onOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
                        onOpenCreateMetricModal={handleOpenCreateMetricModal}
                        dependentKgColumns={dependentKgColumns}
                        placeholder="None"
                      />
                    </Td>
                    <Td>
                      {column.column_type === ColumnType.MEASURE && (
                        <DataModelMenuComponent
                          type="METRIC"
                          logicalColumnList={columns}
                          metricList={getCombinedMetrics(column.header?.id!)}
                          selectedItem={selectedMetrics[index] || null}
                          columnIndex={index}
                          logicalColumn={column}
                          onItemSelect={(selectedMetric) => handleMetricChange(index, selectedMetric)}
                          onOpenCreateKGColumnModal={handleOpenCreateKGColumnModal}
                          onOpenCreateMetricModal={handleOpenCreateMetricModal}
                          placeholder="None"
                        />
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* KG Column creation modal */}
      <Modal isOpen={isKGModalOpen} onClose={onKGModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create KG Column</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isKGModalOpen && (
              <CreateKGColumnComponent
                defaultName={kgModalDefaultName}
                colType={kgModalColumnType}
                refreshKGColumns={refreshKGColumns}
                onClose={onKGModalClose}
                onSuccess={handleKGColumnCreated}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Metric creation modal — pre-selects the triggering logical column as the observation column.
          updateFlag=true  → creates immediately via API (onSuccess auto-maps).
          updateFlag=false → defers to metricRequestList; created after model save (onDefer queues). */}
      <Modal isOpen={isMetricModalOpen} onClose={onMetricModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Metric</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" maxH="80vh">
            {isMetricModalOpen && (
              <EditMetricDefinitionContainer
                createMetricFlag={true}
                modelInfo={{ ...(modelInfo ?? {}), columns } as ModelInfo}
                metricHeader={null}
                onClose={onMetricModalClose}
                onSuccess={updateFlag ? handleMetricCreated : undefined}
                onDefer={!updateFlag ? handleMetricDeferred : undefined}
                defaultObservationColumn={
                  metricModalLogicalColumn?.header?.id
                    ? ObservationColumnProto.create({
                      logical_column_header: { id: metricModalLogicalColumn.header.id },
                      aggregation: AggregationType.SUM,
                    })
                    : undefined
                }
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
export default AddDescription;